/**
 * @fileoverview Search Helper Functions
 *
 * Arama sonuclarini islemek icin yardimci fonksiyonlar.
 * useMapSearch hook'undan extract edildi.
 *
 * @module utils/search-helpers
 */

/**
 * En ucuz N ilani fiyata gore sirali getirir.
 *
 * @param {Array} listings - Ilan listesi
 * @param {number} [count=3] - Getirilecek ilan sayisi
 * @returns {Array<{baslik: string, fiyat: number, m2: number, oda: string, konum: string}>}
 *
 * @example
 * const cheapest = getCheapestListings(listings, 3);
 * // Returns: [{ baslik: "...", fiyat: 1000000, m2: 100, oda: "3+1", konum: "..." }, ...]
 */
export function getCheapestListings(listings, count = 3) {
  return [...listings]
    .filter((l) => l.price && l.price > 0)
    .sort((a, b) => a.price - b.price)
    .slice(0, count)
    .map((l) => ({
      baslik: l.title,
      fiyat: l.price,
      m2: l.m2,
      oda: l.room,
      konum: l.location,
    }));
}

/**
 * Ilanlarin alan (m2) ortalamasini hesaplar.
 *
 * @param {Array} listings - Ilan listesi
 * @returns {number|null} Ortalama alan (m2) veya null
 *
 * @example
 * calculateAreaAverage([{ m2: 100 }, { m2: 150 }]) // 125
 */
export function calculateAreaAverage(listings) {
  const validAreas = listings.filter((l) => l.m2 && l.m2 > 0).map((l) => l.m2);
  if (validAreas.length === 0) return null;
  return Math.round(validAreas.reduce((a, b) => a + b, 0) / validAreas.length);
}

/**
 * Ilanlarin oda sayisi dagilimini hesaplar.
 *
 * @param {Array} listings - Ilan listesi
 * @returns {Object<string, number>} Oda sayisi -> ilan adedi eslesmesi
 *
 * @example
 * calculateRoomDistribution(listings)
 * // Returns: { "2+1": 5, "3+1": 8, "4+1": 3 }
 */
export function calculateRoomDistribution(listings) {
  const distribution = {};
  listings.forEach((l) => {
    if (l.room) {
      distribution[l.room] = (distribution[l.room] || 0) + 1;
    }
  });
  return distribution;
}

/**
 * Konum bilgisi olmadan temel yolu (base path) cikarir.
 *
 * Bounding box (bbox) kullanildiginda konum bilgisine gerek yoktur
 * cunku bbox cografi filtrelemeyi yapar. Bu fonksiyon path'ten
 * sadece islem tipini (satilik-konut, kiralik-daire vb.) cikarir.
 *
 * @param {string|null} path - Tam arama yolu
 * @returns {string|null} Konum bilgisi olmadan temel yol
 *
 * @example
 * getBasePathWithoutLocation("satilik-konut/trabzon")              // "satilik-konut"
 * getBasePathWithoutLocation("satilik-konut/istanbul-kadikoy")     // "satilik-konut"
 * getBasePathWithoutLocation("kiralik-daire/ankara-cankaya-kizilay") // "kiralik-daire"
 * getBasePathWithoutLocation("satilik-villa")                       // "satilik-villa"
 */
export function getBasePathWithoutLocation(path) {
  if (!path) return null;
  // Remove leading slash if present
  const normalized = path.replace(/^\/+/, "");
  // Split by slash - first segment is always the listing type
  const segments = normalized.split("/");
  // Return only the first segment (satilik-konut, kiralik-daire, etc.)
  return segments[0] || null;
}
